// src/context/AppContext.jsx — Firebase version
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { Auth, PackagesDB, ItinerariesDB, EnquiriesDB, DestinationsDB } from '../data/db';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const [packages,     setPackages]     = useState([]);
  const [itineraries,  setItineraries]  = useState([]);
  const [enquiries,    setEnquiries]    = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [isAdmin,      setIsAdmin]      = useState(false);
  const [authLoading,  setAuthLoading]  = useState(true);
  const [dataLoading,  setDataLoading]  = useState(true);

  useEffect(() => {
    // 1. Load public data immediately (packages, itineraries, destinations)
    Promise.all([
      PackagesDB.getAll(),
      ItinerariesDB.getAll(),
      DestinationsDB.getAll(),
    ]).then(([pkgs, itins, dests]) => {
      setPackages(pkgs);
      setItineraries(itins);
      setDestinations(dests);
      setDataLoading(false);
    });

    // 2. Subscribe to Firebase auth state — fires once on load, then on every login/logout
    const unsubscribe = Auth.onAuthChange(async (loggedIn) => {
      setIsAdmin(loggedIn);
      setAuthLoading(false);
      if (loggedIn) {
        const enqs = await EnquiriesDB.getAll();
        setEnquiries(enqs);
      } else {
        setEnquiries([]);
      }
    });

    return () => unsubscribe(); // cleanup on unmount
  }, []);

  // ── Auth ──────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    return Auth.login(email, password);
    // onAuthChange above will automatically set isAdmin + load enquiries
  }, []);

  const logout = useCallback(async () => {
    await Auth.logout();
    // onAuthChange will set isAdmin=false and clear enquiries
  }, []);

  const changePassword = useCallback(async (current, next) => {
    return Auth.changePassword(current, next);
  }, []);

  // ── Packages ──────────────────────────────────────────────────────────────
  const createPackage = useCallback(async (data) => {
    const p = await PackagesDB.create(data);
    if (p) setPackages(prev => [p, ...prev]);
    return p;
  }, []);

  const updatePackage = useCallback(async (id, data) => {
    const p = await PackagesDB.update(id, data);
    if (p) setPackages(prev => prev.map(x => x.id === id ? { ...x, ...p } : x));
  }, []);

  const deletePackage = useCallback(async (id) => {
    await PackagesDB.delete(id);
    setPackages(prev => prev.filter(x => x.id !== id));
  }, []);

  // ── Itineraries ───────────────────────────────────────────────────────────
  const createItinerary = useCallback(async (data) => {
    const i = await ItinerariesDB.create(data);
    if (i) setItineraries(prev => [i, ...prev]);
    return i;
  }, []);

  const updateItinerary = useCallback(async (id, data) => {
    const i = await ItinerariesDB.update(id, data);
    if (i) setItineraries(prev => prev.map(x => x.id === id ? { ...x, ...i } : x));
  }, []);

  const deleteItinerary = useCallback(async (id) => {
    await ItinerariesDB.delete(id);
    setItineraries(prev => prev.filter(x => x.id !== id));
  }, []);

  // ── Enquiries ─────────────────────────────────────────────────────────────
  const createEnquiry = useCallback(async (data) => {
    const e = await EnquiriesDB.create(data);
    // Note: only append to state if admin is viewing — public users don't see the list
    if (e && isAdmin) setEnquiries(prev => [e, ...prev]);
    return e;
  }, [isAdmin]);

  const updateEnquiry = useCallback(async (id, status) => {
    await EnquiriesDB.updateStatus(id, status);
    setEnquiries(prev => prev.map(x => x.id === id ? { ...x, status } : x));
  }, []);

  const deleteEnquiry = useCallback(async (id) => {
    await EnquiriesDB.delete(id);
    setEnquiries(prev => prev.filter(x => x.id !== id));
  }, []);

  // ── Destinations ──────────────────────────────────────────────────────────
  const createDestination = useCallback(async (data) => {
    const d = await DestinationsDB.create(data);
    if (d) setDestinations(prev => [...prev, d]);
  }, []);

  const deleteDestination = useCallback(async (id) => {
    await DestinationsDB.delete(id);
    setDestinations(prev => prev.filter(x => x.id !== id));
  }, []);

  const stats = {
    packages:     packages.length,
    enquiries:    enquiries.length,
    newEnquiries: enquiries.filter(e => e.status === 'new').length,
    itineraries:  itineraries.length,
    destinations: destinations.length,
  };

  return (
    <AppContext.Provider value={{
      packages, itineraries, enquiries, destinations, stats,
      isAdmin, authLoading, dataLoading,
      login, logout, changePassword,
      createPackage, updatePackage, deletePackage,
      createItinerary, updateItinerary, deleteItinerary,
      createEnquiry, updateEnquiry, deleteEnquiry,
      createDestination, deleteDestination,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
};
